import {
  AMENDMENT_DOCUMENT_TYPE,
  COOP_DOCUMENT_TYPE,
  ApsDealDocument,
  DealDocumentSignature,
  DealDocumentSignatureFlowType,
  EnvelopeStatus,
  miscellaneousDocument,
  RecipientStatus,
  SignatureCompletionSource
} from '../models/deal-document.models';

export class DealDocumentHelper {
  static completionSourceLabel(source: SignatureCompletionSource | string | null | undefined): string {
    switch ((source ?? '').toString().trim()) {
      case SignatureCompletionSource.Waived:
        return 'Waived (carbon copy)';
      case SignatureCompletionSource.RemovedFromEnvelope:
        return 'Removed from envelope';
      case SignatureCompletionSource.ExecutedOnPaper:
        return 'Executed on paper';
      case SignatureCompletionSource.Electronic:
        return 'Electronic';
      default:
        return '';
    }
  }

  static recipientStatusRank(status: RecipientStatus | string | null | undefined): number {
    switch ((status ?? '').toString().trim()) {
      case RecipientStatus.Complete:
      case RecipientStatus.Signed:
      case RecipientStatus.Declined:
        return 3;
      case RecipientStatus.Delivered:
      case RecipientStatus.AutoResponded:
      case RecipientStatus.AuthenticationFailed:
        return 2;
      case RecipientStatus.Sent:
        return 1;
      case RecipientStatus.Pending:
      default:
        return 0;
    }
  }

  static isRecipientSigningComplete(status: RecipientStatus | string | null | undefined): boolean {
    const normalized = (status ?? '').toString().trim();
    return normalized === RecipientStatus.Complete || normalized === RecipientStatus.Signed;
  }

  static isExecutiveReleasedStatus(status: RecipientStatus | string | null | undefined): boolean {
    return this.recipientStatusRank(status) >= this.recipientStatusRank(RecipientStatus.Sent);
  }

  static isAmendmentDealDocument(doc: ApsDealDocument): boolean {
    return this.normalizeDealDocumentType(doc.documentType) === AMENDMENT_DOCUMENT_TYPE;
  }

  static isCoopDealDocument(doc: ApsDealDocument): boolean {
    return this.normalizeDealDocumentType(doc.documentType) === COOP_DOCUMENT_TYPE;
  }

  static isCoopTemplate(template: miscellaneousDocument): boolean {
    return this.normalizeDealDocumentType(template.documentType) === COOP_DOCUMENT_TYPE;
  }

  static normalizeDealDocumentType(documentType: string | null | undefined): string {
    return documentType?.trim().toUpperCase() ?? '';
  }

  /** Prefer the document snapshot with the more advanced signing state (API can be stale when only one doc is returned). */
  static mergeNonApsDealDocumentState(cached: ApsDealDocument, fromApi: ApsDealDocument): ApsDealDocument {
    const cachedRank = this.nonApsSigningStateRank(cached);
    const apiRank = this.nonApsSigningStateRank(fromApi);
    const primary = cachedRank >= apiRank ? cached : fromApi;
    const secondary = primary === cached ? fromApi : cached;

    return {
      ...secondary,
      ...primary,
      signingType: cachedRank >= apiRank ? cached.signingType : fromApi.signingType,
      status: cachedRank >= apiRank ? cached.status : fromApi.status,
      // The copy-recipient snapshot only comes from the server, so a cached value
      // (or an older cache entry without the field) must not hide it.
      copyRecipients: fromApi.copyRecipients ?? cached.copyRecipients,
      dealDocumentSignatures: this.mergeDealDocumentSignatures(
        primary.dealDocumentSignatures,
        secondary.dealDocumentSignatures
      )
    };
  }

  /** Merges cached non-APS docs with the latest API payload (API often returns only one document). */
  static mergeDealNonApsDocuments(cached: ApsDealDocument[], fromApi: ApsDealDocument[]): ApsDealDocument[] {
    const byId = new Map<string, ApsDealDocument>();

    for (const doc of fromApi) {
      if (doc?.id) {
        byId.set(doc.id, doc);
      }
    }

    for (const doc of cached) {
      if (!doc?.id) {
        continue;
      }

      if (byId.has(doc.id)) {
        byId.set(doc.id, this.mergeNonApsDealDocumentState(doc, byId.get(doc.id)!));
        continue;
      }

      const cachedType = this.normalizeDealDocumentType(doc.documentType);
      const typeAlreadyFromApi = [...byId.values()].some(
        existing => this.normalizeDealDocumentType(existing.documentType) === cachedType
      );

      if (cachedType === AMENDMENT_DOCUMENT_TYPE || !typeAlreadyFromApi) {
        byId.set(doc.id, doc);
      }
    }

    return [...byId.values()];
  }

  /** True when this deal already has a non-APS document produced from the given project template. */
  static nonApsDealDocumentMatchesTemplate(doc: ApsDealDocument, template: miscellaneousDocument): boolean {
    const tid = doc.templateId?.trim();
    const tplId = template.id?.trim();
    if (tid && tplId && tid === tplId) {
      return true;
    }
    const dt = doc.documentType?.trim().toLowerCase();
    const tdt = template.documentType?.trim().toLowerCase();
    return Boolean(dt && tdt && dt === tdt);
  }

  /** Project COOP templates that do not yet have a matching generated deal document. */
  static filterMiscellaneousTemplatesPendingGeneration(
    templates: miscellaneousDocument[],
    dealDocuments: ApsDealDocument[]
  ): miscellaneousDocument[] {
    return templates.filter(
      template =>
        this.isCoopTemplate(template) &&
        !dealDocuments.some(doc => this.nonApsDealDocumentMatchesTemplate(doc, template))
    );
  }

  private static nonApsSigningStateRank(doc: ApsDealDocument): number {
    if (doc.status === EnvelopeStatus.Complete) {
      return 4;
    }
    if (
      doc.signingType !== DealDocumentSignatureFlowType.None &&
      (doc.status === EnvelopeStatus.Sent || doc.status === EnvelopeStatus.Delivered)
    ) {
      return 3;
    }
    if (doc.signingType !== DealDocumentSignatureFlowType.None) {
      return 2;
    }
    if (doc.dealDocumentSignatures?.some(sig => sig.status === RecipientStatus.Complete)) {
      return 1;
    }
    return 0;
  }

  private static mergeDealDocumentSignatures(
    preferred: DealDocumentSignature[],
    fallback: DealDocumentSignature[]
  ): DealDocumentSignature[] {
    const byId = new Map<string, DealDocumentSignature>();
    for (const sig of fallback) {
      if (sig?.id) {
        byId.set(sig.id, sig);
      }
    }
    for (const sig of preferred) {
      if (!sig?.id) {
        continue;
      }
      const existing = byId.get(sig.id);
      if (!existing) {
        byId.set(sig.id, sig);
        continue;
      }

      const preferredRank = this.recipientStatusRank(sig.status);
      const existingRank = this.recipientStatusRank(existing.status);
      if (preferredRank > existingRank) {
        byId.set(sig.id, sig);
        continue;
      }
      if (existingRank > preferredRank) {
        continue;
      }

      const usePreferred =
        sig.status === RecipientStatus.Complete ||
        (existing.status !== RecipientStatus.Complete && !existing.failureReason);
      byId.set(sig.id, usePreferred ? sig : existing);
    }
    return [...byId.values()];
  }
}
